import React, { useState, useEffect, useMemo } from 'react';

const PHASE_NAMES = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

const getPhaseOptions = (count: number): string[] => {
  return PHASE_NAMES.slice(0, count);
};

type Props = {
  isConnected: boolean;
  isGenerating: boolean;
  setAutoLoopState: (state: any) => void;
  setCurrentLoopPhase: (phase: string) => void;
  setLoopCount: (count: number) => void;
  onDirectSend: (text: string) => void;
  phaseCount?: number;
};

type ConfirmModalState = {
  isOpen: boolean;
  phase: string;
  actionName: string;
  onConfirm: () => void;
};

const ALL_SINGLE_STEP_OPTIONS = [
  { label: '正方3备选', text: '正方3个备选方案' },
  { label: '正方选最优', text: '正方最优方案' },
  { label: '反方3备选', text: '反方3个备选方案' },
  { label: '反方选最优', text: '反方最优方案' },
  { label: '第三方打分', text: '第三方评审' },
  { label: '耻辱重修', text: '重新迭代' },
  { label: '生成详细剧本', text: '生成详细方案' },
  { label: '审核打分', text: '审核详细方案' },
  { label: '根据反馈重写', text: '优化详细方案' },
];

const PHASE_STORAGE_KEY = 'workshop-plan-selected-phase';

export const PhaseControls: React.FC<Props> = ({
  isConnected, isGenerating, setAutoLoopState, setCurrentLoopPhase, setLoopCount, onDirectSend, phaseCount = 7
}) => {
  const phaseOptions = useMemo(() => getPhaseOptions(phaseCount), [phaseCount]);

  const [selectedPhase, setSelectedPhase] = useState(() => {
    const saved = localStorage.getItem(PHASE_STORAGE_KEY);
    return saved && phaseOptions.includes(saved) ? saved : phaseOptions[0];
  });
  const [singleStepSelected, setSingleStepSelected] = useState(ALL_SINGLE_STEP_OPTIONS[0].label);
  
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    isOpen: false,
    phase: '',
    actionName: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    localStorage.setItem(PHASE_STORAGE_KEY, selectedPhase);
  }, [selectedPhase]);

  const requestAction = (actionName: string, actionCallback: () => void) => {
    setConfirmModal({
      isOpen: true,
      phase: selectedPhase,
      actionName: actionName,
      onConfirm: actionCallback
    });
  };

  const executeConfirm = () => {
    confirmModal.onConfirm();
    setConfirmModal({ ...confirmModal, isOpen: false });
  };

  const handleSingleStep = (actionText: string) => {
    setAutoLoopState('IDLE');
    onDirectSend(`环节${selectedPhase}${actionText}`);
  };

  const handleSingleStepExecute = () => {
    const option = ALL_SINGLE_STEP_OPTIONS.find(o => o.label === singleStepSelected);
    if (option) {
      requestAction(option.label, () => handleSingleStep(option.text));
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2 p-2 bg-gray-100 rounded-lg border border-gray-200 shadow-inner relative">

        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide items-center border-l-4 border-indigo-400 pl-2">
          <select 
            value={selectedPhase} 
            onChange={(e) => setSelectedPhase(e.target.value)}
            disabled={!isConnected || isGenerating}
            className="text-sm font-semibold text-brand-700 bg-white border border-brand-300 rounded-md py-1 px-3 shadow-sm outline-none cursor-pointer"
          >
            {phaseOptions.map(p => (
              <option key={p} value={p}>环节{p}</option>
            ))}
          </select>

          <button
            onClick={() => requestAction('🚀 自动对抗闭环', () => {
              setCurrentLoopPhase(selectedPhase);
              setAutoLoopState('PRO_GEN');
              setLoopCount(0);
              onDirectSend(`环节${selectedPhase}正方3个备选方案`);
            })}
            disabled={!isConnected || isGenerating}
            className="whitespace-nowrap px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-blue-500 text-white text-xs font-bold rounded-full hover:shadow-md transition-all disabled:opacity-50"
          >
            🚀 自动对抗闭环
          </button>

          <button
            onClick={() => requestAction('🚀 自动详细设计闭环', () => {
              setCurrentLoopPhase(selectedPhase);
              setAutoLoopState('DETAIL_GEN');
              setLoopCount(0);
              onDirectSend(`环节${selectedPhase}生成详细方案`);
            })}
            disabled={!isConnected || isGenerating}
            className="whitespace-nowrap px-3 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-bold rounded-full hover:shadow-md transition-all disabled:opacity-50"
          >
            🚀 自动详细设计闭环
          </button>

          <div className="flex items-center gap-1">
            <select
              value={singleStepSelected}
              onChange={(e) => setSingleStepSelected(e.target.value)}
              disabled={!isConnected || isGenerating}
              className="text-xs border border-gray-300 rounded-md py-1 px-2 bg-white disabled:opacity-50"
            >
              {ALL_SINGLE_STEP_OPTIONS.map(opt => (
                <option key={opt.label} value={opt.label}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={handleSingleStepExecute}
              disabled={!isConnected || isGenerating}
              className="px-2 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700 disabled:opacity-50"
            >
              执行
            </button>
          </div>
        </div>

      </div>

      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 border border-gray-100 transform scale-100 transition-transform">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="text-xl">⚠️</span> 操作确认
            </h3>
            
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              您即将对 <span className="text-brand-700 font-bold bg-brand-50 px-1.5 py-0.5 rounded text-[15px]">环节{confirmModal.phase}</span> 执行 
              <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded ml-1 text-[15px]">{confirmModal.actionName}</span> 操作。<br/><br/>
              是否确认触发该AI指令？
            </p>

            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                取消操作
              </button>
              <button 
                onClick={executeConfirm}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
              >
                确认执行
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};